<template>
	<Default :loading="loading">
		<div class="w-full h-full relative md:flex">
			<div class="grow flex flex-col md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0 h-full overflow-x-auto">
				<!-- Container -->
				<div class="grow p-3 bg-gray-100">
					<!-- Page header -->
					<div class="md:flex md:items-center">
						<!-- Left: Title -->
						<h1 class="text-2xl md:text-3xl text-gray-800 font-bold mb-2 md:mb-0 flex-1">Registro de Actividad</h1>
						<!-- More actions -->
						<div class="flex flex-col md:flex-row gap-y-2 md:gap-x-2">
							<Button @click="download" :disabled="!user.permissions.includes('user.history.download')" :loading="downloading">
								<icon name="ri-download-line" class="mr-1" />
								Descargar registro
							</Button>
						</div>
					</div>

					<Accordion title="Filtros" class="bg-white mt-3">
						<div class="grid md:grid-cols-2 xl:grid-cols-4 gap-2 my-2">
							<Multiselect v-model="selectedUsers" track-by="_id" :custom-label="u => `${u.name} ${u.surname}`" placeholder="Seleccionar usuarios" :hideSelected="true" :multiple="true" :options="users" :showNoResults="true" :closeOnSelect="false" :preserveSearch="true" :clearOnSelect="false" selectLabel="enter para seleccionar" selectedLabel="seleccionado" deselectLabel="enter para eliminar" @select="onSelectUser" @remove="onSelectUser">
								<template #noResult>Sin resultados. Realiza otra búsqueda. </template>
								<template #noOptions>No se han encontrado estados. </template>
							</Multiselect>

							<Multiselect v-model="selectedEventTypes" track-by="type" :custom-label="u => u.label" placeholder="Seleccionar eventos" :hideSelected="true" :multiple="true" :options="eventTypes" :showNoResults="true" :closeOnSelect="false" :preserveSearch="true" :clearOnSelect="false" selectLabel="enter para seleccionar" selectedLabel="seleccionado" deselectLabel="enter para eliminar" @select="onSelectEventType" @remove="onSelectEventType">
								<template #noResult>Sin resultados. Realiza otra búsqueda. </template>
								<template #noOptions>No se han encontrado estados. </template>
							</Multiselect>

							<div>
								<input class="w-full form-input" type="datetime-local" name="date>" v-model="options['date>']" @input="e => onChangeSearchInput(e, 'date>')" />
								<small class="w-full p-1">Desde</small>
							</div>
							<div>
								<input class="w-full form-input" type="datetime-local" name="date<" v-model="options['date<']" @input="e => onChangeSearchInput(e, 'date<')" />
								<small class="w-full p-1">Hasta</small>
							</div>
						</div>
					</Accordion>

					<!-- Table -->
					<div class="bg-white rounded-sm border border-gray-200 relative mt-3">
						<div class="flex flex-wrap gap-6 items-center justify-between py-2 px-4">
							<header class="flex-1 font-semibold text-slate-800 whitespace-nowrap">
								<div>
									<p>
										Resultados:
										<span class="text-slate-400">{{ total }}</span>
									</p>
								</div>
							</header>

							<div class="gap-2 flex items-center">
								<div>
									Mostrar:
									<select v-model="options.limit" class="ml-2 form-select flex-1 md:flex-auto" @change="onChangeLimit">
										<option :value="5">5</option>
										<option :value="10">10</option>
										<option :value="20">20</option>
										<option :value="50">50</option>
										<option :value="100">100</option>
										<option :value="150">150</option>
									</select>
								</div>
							</div>
						</div>

						<!-- Table -->
						<div class="overflow-x-auto" v-if="!loading && items.length">
							<table class="table-auto w-full">
								<!-- Table header -->
								<thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-b border-t border-gray-200">
									<tr>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('typeLabel')">
													<p class="uppercase mr-1">Tipo</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'typeLabel'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('user')">
													<p class="uppercase mr-1">Email</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'user'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('meta')">
													<p class="uppercase mr-1">Meta</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'meta'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('userAgent')">
													<p class="uppercase mr-1">Agente de Usuario</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'userAgent'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('date')">
													<p class="uppercase mr-1">Fecha</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'date'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('ip')">
													<p class="uppercase mr-1">IP</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'ip'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
									</tr>
								</thead>
								<!-- Table body -->
								<tbody class="text-sm divide-y divide-gray-200">
									<tr class="even:bg-gray-100 hover:bg-gray-200" v-for="item in items" :key="item._id">
										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">{{ item.typeLabel }}</td>
										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">{{ getUserEmail(item.user) }}</td>
										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap place-content-start">
											<VueJsonEditor v-if="Object.keys(item.meta).length > 0" v-model.text="item.meta" mode="text" class="h-full w-full rounded overflow-hidden" :readOnly="true" />
										</td>
										<td class="px-2 first:pl-5 last:pr-5 py-2 place-content-start max-w-56">{{ item.userAgent }}</td>
										<td class="px-2 first:pl-5 last:pr-5 py-2 place-content-start whitespace-nowrap">{{ dayjs(item.date).format('DD/MM/YY - HH:mm') }}</td>
										<td class="px-2 first:pl-5 last:pr-5 py-2 place-content-start whitespace-nowrap">{{ item.ip }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<!-- Pagination -->
					<div class="mt-8 mb-24" v-if="!loading && items.length">
						<Pagination v-show="total > options.limit" :results="items.length" :skip="options.skip" :limit="options.limit" :total="total" @page="updatePage" />
					</div>

					<!-- No results -->
					<div class="bg-white p-4 rounded-sm border border-gray-200 mt-3" v-if="(!loading && !items.length) || !!error.message">
						<Banner type="error" :open="!!error?.message" fixed>
							<ul v-if="error?.errors">
								<li v-for="(err, i) in error?.errors" :key="i">
									<p class="p-3">- {{ err.message }}</p>
								</li>
							</ul>
						</Banner>

						<div class="w-full flex justify-center p-4" v-if="!loading && !items.length">
							<div class="text-center py-6">
								<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-300 to-red-100 mb-4">
									<icon name="ri-information-line" scale="2" />
								</div>
								<h2 class="text-2xl text-gray-800 font-bold mb-2">No se han encontrado proyectos.</h2>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Default>
</template>

<script setup lang="ts">
import { ValidationError } from '@/errors/ValidationError'
import { user } from '@/user'
import dayjs from 'dayjs'
import VueJsonEditor from 'json-editor-vue'
import { Ref, onMounted, reactive, ref, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import { useRoute, useRouter } from 'vue-router'
import Accordion from '../components/Accordion.vue'
import Banner from '../components/Banner.vue'
import Button from '../components/Button.vue'
import Pagination from '../components/Pagination.vue'
import { History } from '../interfaces/History'
import { User } from '../interfaces/User'
import { get } from '../services/api'
import Default from './Default.vue'

const router = useRouter()
const route = useRoute()

const options: Record<string, any> = reactive({
	...route.query,
	limit: 20,
	skip: 0,
	sort: route.query.sort || '-date',
	...(route.query['date>'] && { ['date>']: route.query['date>'] }),
	...(route.query['date<'] && { ['date<']: route.query['date<'] })
})

type EventType = {
	_id: string
	type: string
	label: string
}

const loading = ref(false)
const downloading = ref(false)
const items = ref<History[]>([])
const total = ref(0)
const error = ref<ValidationError>(<any>{})
const sortBy = reactive({ field: (<string>options.sort || '')?.split('-')[1], type: options.sort?.includes('-') ? '-' : '' })
const users = ref<User[]>([])
const hasUserPermission = ref<boolean>(false)
const selectedUsers = ref(null)
const selectedEventTypes = ref(null)
const eventTypes = ref<EventType[]>([])

const onSelectUser = () => {
	const userList = selectedUsers.value.map((user: User) => user._id)
	options.user = userList.length ? userList.join(',') : undefined
}

const onSelectEventType = () => {
	const eventTypeList = selectedEventTypes.value.map((eventType: EventType) => eventType.type)
	options.type = eventTypeList.length ? eventTypeList.join(',') : undefined
}

const handleSort = column => {
	if (column != sortBy.field) {
		sortBy.field = column
		sortBy.type = ''
	} else {
		sortBy.type = sortBy.type === '-' ? '' : '-'
	}
	options.sort = `${sortBy.type}${sortBy.field}`
}

const search = async () => {
	loading.value = true
	error.value = <any>{}
	try {
		await router.replace({
			query: options
		})
		items.value = []

		const sendOptions = { ...options }

		if (options['date<']) {
			sendOptions['date<'] = new Date(options['date<']).toISOString()
		}
		if (options['date>']) {
			sendOptions['date>'] = new Date(options['date>']).toISOString()
		}

		const { data, pagination } = await get({ name: 'history', query: sendOptions })
		total.value = pagination.total
		items.value = data
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const onChangeSearchInput = (e: any, option?: string) => {
	if (!e.target.value) {
		switch (option) {
			case 'date>':
				delete options['date>']
				break
			case 'date<':
				delete options['date<']
				break
		}
	}
}

const download = async () => {
	downloading.value = true
	try {
		const { limit, skip, ...sendOptions } = options

		if (options['date<']) {
			sendOptions['date<'] = new Date(options['date<']).toISOString()
		}
		if (options['date>']) {
			sendOptions['date>'] = new Date(options['date>']).toISOString()
		}
		const { data } = await get({ name: 'history.download', query: sendOptions })
		const base64Uri = `data:@file/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`
		const blob = await fetch(base64Uri).then(res => res.blob())
		const link = document.createElement('a')
		link.href = window.URL.createObjectURL(blob)
		link.download = `USERS_HISTORY_${dayjs().format('DD-MM-YYYY')}.xlsx`
		link.click()
		link.remove()
	} catch (err) {
		error.value = err
	}
	downloading.value = false
}

const fetchUsers = async () => {
	loading.value = true
	try {
		const { data } = await get({ name: 'user' })
		users.value = data
		selectedUsers.value = [...((<string>route.query.user)?.split(',').length ? users.value.filter(u => route.query.user?.includes(u._id)) : [])]
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const fetchHistoryEventTypes = async () => {
	loading.value = true
	try {
		const { data } = await get({ name: 'history.events' })
		eventTypes.value = data
		selectedEventTypes.value = [...((<string>route.query.type)?.split(',').length ? eventTypes.value.filter(e => route.query.type?.includes(e.type)) : [])]
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const getUserEmail = (_id: string) => {
	if (hasUserPermission.value) {
		const user = users.value.find(u => u._id === _id)
		if (user) {
			return user.email
		}
	}
	return _id
}

onMounted(() => {
	search()
	if (user.value.permissions.includes('user.find')) {
		hasUserPermission.value = true
		fetchUsers()
	}
	fetchHistoryEventTypes()
})

watch(options, () => {
	search()
})

const onChangeLimit = () => {
	options.skip = 0
}

const updatePage = (page: Ref) => {
	options.skip = page.value == -1 ? 0 : (page.value - 1) * options.limit
}
</script>
